import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import LoadingSpinner from '@/shared/icons/LoadingSpinner';
import WarningTriangleIcon from '@/shared/icons/WarningTriangleIcon';
import DTooltip from '@/components/Global/DTooltip';
import * as chatbotService from '@/services/chatbot.service';
import { useUserStore } from '@/stores/user/userStore';
import { useJobsStore } from '@/stores/chatbot/jobsStore';
import { useJobsData } from '@/hooks/useJobsData';
import { useKnowledgeBaseJobStatus } from '@/hooks/useKnowledgeBaseJobStatus';
import { useNavigate, useParams } from 'react-router-dom';
import { GENERAL_POLLING_INTERVAL } from '@/constants';
import ChatIconTopNav from '@/shared/icons/ChatIconTopNav';
import IconWrapper from '@/shared/components/UI/IconWrapper';
import ChevronDownIcon from '@/shared/icons/ChevronDownIcon';
import StatusIcon from '@/shared/icons/StatusIcon';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import RefreshIcon from '@/shared/icons/RefreshIcon';
import LeftArrowIcon from '@/shared/icons/LeftArrowIcon';
import useModalStore from '@/stores/modal/modalStore';
import CurvedBackIcon from '@/shared/icons/CurvedBackIcon';
import useTeamManagementStore from '@/stores/teamManagement/teamManagementStore';
import useDanteApi from '@/hooks/useDanteApi';

import RightChevronIcon from '@/shared/icons/RightChevronIcon';
import UpgradeIcon from '@/shared/icons/UpgradeIcon';
import ChatbotInactiveBadge from '@/v3-dashboard/components/UI/ChatbotInactiveBadge';
import { getNumericalFeatureValue } from '@/helpers/tier/featureCheck';

const RunningJobsDropdown = ({ mode = 'per_user', chatbotId = null, isMobileView = false, isMobileButton = false, onMobileButtonClick, onBack, onClose }) => {
  const navigate = useNavigate();
  const params = useParams();
  const user = useUserStore(state => state.user);
  const selectedChatbot = useChatbotStore(state => state.selectedChatbot);
  const openPlansModal = useModalStore((state) => state.openPlansModal);
  const selectedTeam = useTeamManagementStore((state) => state.selectedTeam);

  // In team context, use owner's tier limit for chatbot total
  const totalChatbots = selectedTeam?.id
    ? getNumericalFeatureValue('number_of_knowledgebases')
    : (user?.total_ai_chatbots || 0);
  
  // Fetch chatbots
  const { data: chatbotsData, isLoading: isLoadingChatbots } = useDanteApi(
    chatbotService.getChatbotsDashboard,
    [selectedTeam]
  );
  const chatbots = chatbotsData?.results || [];

  // Get max characters from user tier (same as ChatbotKnowledge.jsx)
  const maxCharactersPerKB = user?.characters_per_knowledgebase || 0;
  const { setJobsData, setPollingInterval, clearPollingInterval } = useJobsStore();
  const jobsPollingIntervalRef = useRef(null);

  // Use chatbotId from props or from URL params
  const currentChatbotId = chatbotId || params.id;

  // Prefer the chatbot from the current route to avoid stale flicker
  const resolvedChatbot = useMemo(() => {
    if (!currentChatbotId) return selectedChatbot;
    const foundChatbot = chatbots.find((chatbot) => {
      const resolvedId = chatbot.kb_id || chatbot.id;
      return String(resolvedId) === String(currentChatbotId);
    });

    const selectedChatbotId =
      selectedChatbot?.kb_id ||
      selectedChatbot?.knowledge_base?.id ||
      selectedChatbot?.id;
    const selectedMatchesCurrent =
      selectedChatbotId && String(selectedChatbotId) === String(currentChatbotId);
    return foundChatbot || (selectedMatchesCurrent ? selectedChatbot : null);
  }, [chatbots, currentChatbotId, selectedChatbot]);

  // Use knowledge base job status hook for per_chatbot mode
  const { jobStatusData } = useKnowledgeBaseJobStatus(
    mode === 'per_chatbot' ? currentChatbotId : null
  );

  const startJobsPollingPerChatbot = useCallback(async () => {
    try {
      if (!currentChatbotId) return;

      const response = await chatbotService.getJobsStatusPerChatbot(currentChatbotId);
      setJobsData(currentChatbotId, response.data);

      // Continue polling regardless of job status to pick up new jobs
    } catch (error) {
      console.error('Error fetching jobs status:', error);
      if (error.response?.status === 404) {
        setJobsData(currentChatbotId, { results: [] });
      }
    }
  }, [currentChatbotId, setJobsData]);

  // Get jobs data from store based on mode
  const chatbotJobsData = useJobsData(currentChatbotId);
  const userJobsData = useJobsData('user');
  const jobsData = mode === 'per_chatbot' ? chatbotJobsData : userJobsData;

  // Filter running jobs (not completed, failed, or canceled)
  const runningJobs = jobsData?.results?.filter(job =>
    job.status !== 'completed' &&
    job.status !== 'failed' &&
    job.status !== 'canceled' &&
    job.status !== 'error'
  ) || [];

  // Poll jobs status for user (all chatbots)
  const startJobsPollingPerUser = useCallback(async () => {
    try {
      if (!user?.id) return;

      const response = await chatbotService.getJobsStatusPerUser(user.id);
      // For user mode, we store data with a special key
      setJobsData('user', response.data);

    } catch (error) {
      console.error('Error fetching jobs status:', error);
    }
  }, [user?.id, setJobsData]);

  // Start jobs polling on mount based on effective mode
  useEffect(() => {
    // Clear any existing interval first
    if (jobsPollingIntervalRef.current) {
      clearInterval(jobsPollingIntervalRef.current);
      jobsPollingIntervalRef.current = null;
    }

    // Determine which polling function to use based on effective mode
    const pollingFunction = mode === 'per_chatbot' ? startJobsPollingPerChatbot : startJobsPollingPerUser;

    // Check if we have the necessary data to start polling
    if ((mode === 'per_user' && user?.id) || (mode === 'per_chatbot' && currentChatbotId)) {
      // Initial fetch
      pollingFunction();

      // Set up polling interval
      const interval = setInterval(() => {
        pollingFunction();
      }, GENERAL_POLLING_INTERVAL);

      jobsPollingIntervalRef.current = interval;

      // Store the interval in the store for cleanup
      if (mode === 'per_chatbot' && currentChatbotId) {
        setPollingInterval(currentChatbotId, interval);
      }
    }

    // Cleanup function for jobs polling
    return () => {
      if (jobsPollingIntervalRef.current) {
        clearInterval(jobsPollingIntervalRef.current);
        jobsPollingIntervalRef.current = null;
      }

      // Clear from store as well
      if (mode === 'per_chatbot' && currentChatbotId) {
        clearPollingInterval(currentChatbotId);
      }
    };
  }, [mode, user?.id, currentChatbotId, startJobsPollingPerChatbot, startJobsPollingPerUser, setPollingInterval, clearPollingInterval]);

  // Hide component in per_user mode if no chatbot is selected (check both kb_id and knowledge_base.id)
  if (mode === 'per_user' && !resolvedChatbot?.kb_id && !resolvedChatbot?.knowledge_base?.id) {
    return null;
  }

  // Component to render a single running job
  const RunningJobCard = ({ job }) => (
    <div className="flex items-center justify-between w-full ">
      {/* Chatbot name on the left */}
      <span className="text-sm font-medium text-v3-black-max">
        {job.knowledge_base_name}
      </span>

      {/* Training status on the right */}
      <div className="flex items-center gap-2 border border-v3-card-border rounded-v3-sm px-v3-xs py-[7px] ">
          <IconWrapper size={20}>
            <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
          </IconWrapper>
        <span className="text-sm font-medium text-v3-purple-primary ">
          Training...
        </span>
      </div>
    </div>
  );

  // Shared MenuItems component
  const JobsMenuItems = ({ position = "right", close }) => {
    const anchorPosition = position === "right" ? "bottom end" : "bottom start";

    const handleCreateChatbot = () => {
      navigate('/chatbot/create');
      close?.();
    };

    const handleJobClick = (jobId) => {
      navigate(`/training/${jobId}`);
      close?.();
    };

    // Check if selected chatbot is currently training
    // Compare by both ID and name to handle different API response formats
    const selectedChatbotKbId = resolvedChatbot?.kb_id || resolvedChatbot?.knowledge_base?.id;
    const selectedChatbotName = resolvedChatbot?.knowledge_base?.knowledge_base_name || resolvedChatbot?.name;

    // Find the running job for the selected chatbot (to get job_id for navigation)
    const selectedChatbotJob = runningJobs.find(job => {
      const jobKbId = job.knowledge_base_id || job.kb_id || job.knowledgebase_id;
      return jobKbId === selectedChatbotKbId || job.knowledge_base_name === selectedChatbotName;
    });

    const isSelectedChatbotTraining = !!selectedChatbotJob;

    // Filter out the selected chatbot from running jobs list (since it's shown in the header)
    const otherRunningJobs = runningJobs.filter(job => {
      const jobKbId = job.knowledge_base_id || job.kb_id || job.knowledgebase_id;
      // Filter by ID or by name
      return jobKbId !== selectedChatbotKbId && job.knowledge_base_name !== selectedChatbotName;
    });

    // Handler to navigate to training page for selected chatbot
    const handleTrainingClick = (e) => {
      e.stopPropagation();
      if (selectedChatbotJob?.job_id) {
        navigate(`/training/${selectedChatbotJob.job_id}`);
        close?.();
      }
    };

    return (
      <MenuItems
        anchor={anchorPosition}
        className="w-[400px] origin-top-right bg-white border border-v3-card-border  shadow-v3-card rounded-v3-sm overflow-y-auto z-20 focus:outline-none transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] data-[closed]:scale-95 data-[closed]:opacity-0 mt-[18px]"
      >
        {/* Agent Usage Section */}
        <div className="flex flex-row justify-between items-center h-[48px] px-v3-sm py-v3-xs">
          <p className="text-v3-black-secondary text-[14px] font-semibold leading-5">
            Agents used
          </p>
          <div className="flex h-8 py-[7px] items-center gap-1">
            <span className="text-v3-black-secondary text-[14px] font-semibold leading-5">
              {user?.used_ai_chatbots || 0}/{totalChatbots}
            </span>
            <button
              onClick={() => openPlansModal('number_of_knowledgebases')}
              className="cursor-pointer hover:text-v3-purple-medium transition-opacity flex items-center"
              aria-label="Upgrade plan"
            >
              <IconWrapper size={20}>
              <UpgradeIcon className="text-v3-purple-primary hover:text-v3-purple-medium" />
              </IconWrapper>
            </button>
          </div>
        </div>
         <div className='bg-v3-black-lightest border border-v3-card-border rounded-v3-sm px-v3-sm py-v3-xs'>
            <div className='flex items-center justify-between pb-v3-xs flex-wrap gap-v3-xs'>
              <p className='text-v3-black-max text-v3-h9 font-medium'>{resolvedChatbot?.knowledge_base?.knowledge_base_name || resolvedChatbot?.name || 'Agent'}</p>
              <div className="flex items-center gap-v3-xs">
                <div
                  className={`flex items-center gap-v3-xxs border border-v3-card-border rounded-v3-sm px-v3-xs py-v3-[7px] h-v3-lg bg-white ${isSelectedChatbotTraining ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  onClick={isSelectedChatbotTraining ? handleTrainingClick : undefined}
                >
                  {isSelectedChatbotTraining ? (
                    <>
                      <IconWrapper size={16}>
                        <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
                      </IconWrapper>
                      <p className="text-v3-h10 font-medium text-v3-purple-primary">Training...</p>
                    </>
                  ) : (
                    <>
                      <IconWrapper size={16}>
                        <StatusIcon className="w-full h-full text-v3-green-primary" />
                      </IconWrapper>
                      <p className="text-v3-h10 font-medium text-v3-black-max">Trained</p>
                    </>
                  )}
                </div>
                {resolvedChatbot?.active_chatbot === false && <ChatbotInactiveBadge />}
              </div>
            </div>
            <div className='flex flex-col gap-[12px] pb-v3-xs'>
              <div className='w-full h-[4px] bg-white rounded-full'>
                <div
                  className='h-full bg-v3-green-primary rounded-full transition-all duration-300'
                  style={{
                    width: `${Math.min(100, Math.max(0, ((resolvedChatbot?.knowledge_base?.num_chars || 0) / maxCharactersPerKB) * 100))}%`
                  }}
                ></div>
              </div>
              <div className='flex items-center justify-between'>
                <p className='text-v3-black-secondary text-v3-h9'>Memory used</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openPlansModal();
                    close?.();
                  }}
                  className='text-v3-purple-primary text-v3-h9 font-medium hover:text-v3-purple-medium transition-colors cursor-pointer'
                >
                  {(selectedChatbot?.knowledge_base?.num_chars || 0).toLocaleString()}/{maxCharactersPerKB.toLocaleString()}
                </button>
              </div>
            </div>
         </div>
        {otherRunningJobs.length > 0 ? (
          <>
            <div>
              {otherRunningJobs.map((job) => (
                <MenuItem key={job.job_id} as="div" className="px-3 py-2 hover:bg-v3-menu-hover cursor-pointer" onClick={() => handleJobClick(job.job_id)}>
                  <RunningJobCard job={job} />
                </MenuItem>
              ))}
            </div>
          </>
        ) : null}
        
        {/* All Chatbots List */}
        {chatbots.length > 0 ? (
            <div className='max-h-[200px] overflow-y-auto'>
              {chatbots
                .filter((chatbot) => {
                  const chatbotId = chatbot.kb_id || chatbot.id;
                  const selectedChatbotId = resolvedChatbot?.kb_id || resolvedChatbot?.knowledge_base?.id;
                  // Filter out the selected chatbot since it's already shown at the top
                  return chatbotId !== selectedChatbotId;
                })
                .map((chatbot) => {
                  const chatbotId = chatbot.kb_id || chatbot.id;
                  const chatbotName = chatbot.knowledge_base?.knowledge_base_name || chatbot.name || 'Unnamed Agent';
                  const isSelected = false; // No longer needed since we filtered it out, but keeping for consistency
                
                return (
                  <MenuItem key={chatbotId} as="div">
                    {({ focus }) => (
                      <button
                        onClick={() => {
                          navigate(`/chatbot/${chatbotId}`);
                          close?.();
                        }}
                        className={`flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm rounded-v3-sm transition-colors w-full hover:bg-v3-menu-hover h-[48px] ${
                          isSelected
                            ? 'bg-v3-menu-selected'
                            : ''
                        }`}
                      >
                        <span className={`text-sm font-medium truncate ${isSelected ? 'text-v3-black-max' : 'text-v3-black-max'}`}>
                          {chatbotName}
                        </span>
                      </button>
                    )}
                  </MenuItem>
                );
              })}
            </div>
        ) : isLoadingChatbots ? (
          <div className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm">
            <div className="flex h-8 py-[7px] items-center gap-v3-xxs">
              <IconWrapper size={20}>
                <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
              </IconWrapper>
              <span className="text-v3-black-max text-v3-h10 font-[510]">
                Loading more agents...
              </span>
            </div>
          </div>
        ) : null}
        
        <div className='p-v3-sm'>
          <button
            onClick={handleCreateChatbot}
            className='w-full text-center text-v3-purple-primary text-v3-h10 font-medium hover:opacity-80 transition-opacity py-[10px]'
          >
            + Create New Agent
          </button>
        </div>
      </MenuItems>
    );
  };


  // Per-chatbot mode with different UI
  if (mode === 'per_chatbot') {
    if (!jobsData) {
      return <div className="h-[34px] mt-[6px]"></div>;
    }
    if ( jobsData.results.length === 0) {
      // Check if there are any failed or skipped URLs in the job status
      const hasWarnings = jobStatusData && (
        (jobStatusData.failed_urls > 0) ||
        (jobStatusData.skipped_urls > 0) ||
        (jobStatusData.job_summary?.failed?.length > 0) ||
        (jobStatusData.job_summary?.skipped?.length > 0)
      );

      // Create tooltip content for warnings
      const warningTooltip = hasWarnings ? (
        <div className="flex flex-col gap-1 my-1">
          {(jobStatusData.failed_urls > 0 || jobStatusData.job_summary?.failed?.length > 0) && (
            <p className='text-base'>Failed URLs: <b>{jobStatusData.failed_urls || jobStatusData.job_summary?.failed?.length || 0}</b></p>
          )}
          {(jobStatusData.skipped_urls > 0 || jobStatusData.job_summary?.skipped?.length > 0) && (
            <p className='text-base'>Skipped URLs: <b>{jobStatusData.skipped_urls || jobStatusData.job_summary?.skipped?.length || 0}</b></p>
          )}
          {jobStatusData.job_id && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/training/${jobStatusData.job_id}`);
              }}
              className="text-white/80 text-base mt-1 text-left font-medium underline"
            >
              View Details &rarr;
            </button>
          )}
        </div>
      ) : null;

      return (
        <div className="flex items-center gap-1">
          <p className="text-green-500 font-medium bg-green-10 rounded-full px-v3-sm py-v3-xxs flex items-center gap-v3-xxs w-[90px] h-[34px] mt-[6px]">
           <span className='text-green-500 text-sm font-medium'>&#x2022;</span>
            <span className='text-green-500 text-sm font-medium'>Trained</span>
          </p>
          {hasWarnings && (
            <DTooltip content={warningTooltip} position="right">
              <WarningTriangleIcon className="w-4 h-4 text-orange-400 mt-[6px]" />
            </DTooltip>
          )}
        </div>
      );
    }

    // If we have data, show button with count
    const totalUrls = runningJobs.reduce((acc, job) => acc + (job.total_discovered_urls || 0), 0);
    const completedUrls = runningJobs.reduce((acc, job) =>
      acc + ((job.urls_completed || 0) + (job.urls_failed || 0) + (job.urls_skipped || 0)), 0);

    return (
      <Menu as="div" className="relative">
        {({ close }) => (
          <>
            <MenuButton className="text-accent font-medium bg-purple-10 rounded-full px-v3-sm py-v3-xxs flex items-center gap-v3-xxs justify-between w-full h-[34px] mt-[6px]">
              <span className='text-accent text-sm font-medium flex items-center gap-v3-xxs'>
                <span className='text-accent text-sm font-medium animate-scale-gently'>&#x2022;</span>
                <span> Training</span>
                <span className="after:animate-ellipsis-cycle after:content-[''] -ml-1 inline-block w-[18px] text-left"></span>
              </span>
              {totalUrls > 0 ? (
                <span className='text-accent text-sm font-medium'>{completedUrls}/{totalUrls}</span>
              ) : (
                <LoadingSpinner className="h-4 w-4 text-accent" />
              )}
            </MenuButton>
            <JobsMenuItems position="left" close={close} />
          </>
        )}
      </Menu>
    );
  }

  // Per-user mode - Mobile button only (for main nav view)
  if (isMobileView && isMobileButton) {
    // Check if chatbot is inactive for mobile button view
    const isChatbotInactiveMobile = resolvedChatbot?.active_chatbot === false;
    
    return (
      <button
        onClick={onMobileButtonClick}
        className="flex items-center justify-between w-full text-v3-h10 py-[8px]  font-regular rounded transition-colors hover:bg-v3-menu-hover cursor-pointer"
      >
        <div className="flex items-center gap-v3-xs">
          <div className='w-[20px] h-[20px]'>
             <IconWrapper size={9}>
            <CurvedBackIcon className="w-full h-full" />
          </IconWrapper>
          </div>
          <IconWrapper size={20}>
            <ChatIconTopNav className="w-full h-full text-v3-disabled-text" />
          </IconWrapper>
        <p className="text-v3-h10 font-medium text-v3-black-max">
          {resolvedChatbot?.knowledge_base?.knowledge_base_name || resolvedChatbot?.name || 'Agent'}
        </p>
        {!isChatbotInactiveMobile && (
          <div className='flex items-center gap-v3-xxs border border-v3-card-border rounded-v3-sm px-[12px] py-v3-xxs h-v3-lg'>
            <IconWrapper size={20}>
              <StatusIcon className="w-full h-full text-v3-green-primary" />
            </IconWrapper>
            <p className="text-v3-h10 font-medium text-v3-black-max">Trained</p>
          </div>
        )}
        {isChatbotInactiveMobile && <ChatbotInactiveBadge />}
        </div>
        <IconWrapper size={20}>
          <RightChevronIcon className="w-full h-full text-v3-purple-primary" />
        </IconWrapper>
      </button>
    );
  }

  // Per-user mode - Mobile full-page view
  // Check if selected chatbot is currently training (for mobile and desktop views)
  // Compare by both ID and name to handle different API response formats
  const selectedChatbotKbId = resolvedChatbot?.kb_id || resolvedChatbot?.knowledge_base?.id;
  const selectedChatbotName = resolvedChatbot?.knowledge_base?.knowledge_base_name || resolvedChatbot?.name;

  // Find the running job for the selected chatbot (to get job_id for navigation)
  const selectedChatbotJob = runningJobs.find(job => {
    const jobKbId = job.knowledge_base_id || job.kb_id || job.knowledgebase_id;
    return jobKbId === selectedChatbotKbId || job.knowledge_base_name === selectedChatbotName;
  });

  const isSelectedChatbotTraining = !!selectedChatbotJob;

  // Filter out the selected chatbot from running jobs list (since it's shown in the header)
  const otherRunningJobs = runningJobs.filter(job => {
    const jobKbId = job.knowledge_base_id || job.kb_id || job.knowledgebase_id;
    // Filter by ID or by name
    return jobKbId !== selectedChatbotKbId && job.knowledge_base_name !== selectedChatbotName;
  });

  // Handler to navigate to training page for selected chatbot
  const handleTrainingStatusClick = () => {
    if (selectedChatbotJob?.job_id) {
      navigate(`/training/${selectedChatbotJob.job_id}`);
    }
  };

  if (isMobileView) {
    const chatbotName = selectedChatbot?.knowledge_base?.knowledge_base_name || selectedChatbot?.name || 'Agent';
    const numChars = selectedChatbot?.knowledge_base?.num_chars || 0;
    const memoryPercentage = Math.min(100, Math.max(0, (numChars / maxCharactersPerKB) * 100));

    return (
      <div className="md:hidden w-full h-full flex flex-col overflow-y-auto bg-white sm:px-v3-lg px-v3-sm">
        {/* Back Button */}
        <div className="h-[40px] my-[12px]">
          <button onClick={onBack} className="flex items-center gap-v3-xs text-v3-purple-primary  py-[10px]">
            <IconWrapper size={20}>
              <LeftArrowIcon className="w-full h-full text-v3-purple-primary" />
            </IconWrapper>
            <span className="text-v3-h10 font-medium">Back</span>
          </button>
        </div>

        {/* Chatbot Info */}
        <div className="flex-1 flex flex-col">
          
          {/* Agent Usage Section */}
          <div className="flex items-center px-v3-sm py-v3-md justify-between">
            <p className="text-v3-black-secondary text-[14px] font-semibold leading-5">
              Agents used
            </p>
            <div className="flex h-8 py-[7px] items-center gap-1">
              <span className="text-v3-black-secondary text-[14px] font-semibold leading-5">
                {user?.used_ai_chatbots || 0}/{totalChatbots}
              </span>
              <button
                onClick={() => openPlansModal('number_of_knowledgebases')}
                className="cursor-pointer hover:text-v3-purple-medium transition-opacity flex items-center"
                aria-label="Upgrade plan"
              >
                <IconWrapper size={20}>
                  <UpgradeIcon className="text-v3-purple-primary hover:text-v3-purple-medium" />
                </IconWrapper>
              </button>
            </div>
          </div>

          {/* Selected Chatbot Header */}
          <div className="bg-v3-black-lightest  border border-v3-card-border rounded-v3-sm px-v3-sm py-[12px]">
            <div className="flex items-center justify-between pb-v3-sm flex-wrap gap-v3-xs">
              <div className="flex items-center gap-v3-xs">
                {/* <IconWrapper size={9}>
                  <CurvedBackIcon className="w-full h-full" />
                </IconWrapper> */}
                <p className="text-v3-black-max text-v3-h9 font-medium">{chatbotName}</p>
              </div>
              <div className="flex items-center gap-v3-xs">
                <div
                  className={`flex items-center gap-v3-xxs border border-v3-card-border rounded-v3-sm px-[12px] py-v3-xxs h-v3-lg ${isSelectedChatbotTraining ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                  onClick={isSelectedChatbotTraining ? handleTrainingStatusClick : undefined}
                >
                  {isSelectedChatbotTraining ? (
                    <>
                      <IconWrapper size={16}>
                        <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
                      </IconWrapper>
                      <p className="text-v3-h10 font-medium text-v3-purple-primary">Training...</p>
                    </>
                  ) : (
                    <>
                      <IconWrapper size={16}>
                        <StatusIcon className="w-full h-full text-v3-green-primary" />
                      </IconWrapper>
                      <p className="text-v3-h10 font-medium text-v3-black-max">Trained</p>
                    </>
                  )}
                </div>
                {resolvedChatbot?.active_chatbot === false && <ChatbotInactiveBadge />}
              </div>
            </div>

            {/* Memory Progress Bar */}
            <div className="flex flex-col gap-[12px] pb-v3-xs">
              <div className="w-full h-[4px] bg-white rounded-full">
                <div
                  className="h-full bg-v3-green-primary rounded-full transition-all duration-300"
                  style={{ width: `${memoryPercentage}%` }}
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-v3-black-secondary text-v3-h9">Memory used</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openPlansModal();
                  }}
                  className="text-v3-purple-primary text-v3-h9 font-medium hover:text-v3-purple-medium transition-colors cursor-pointer"
                >
                  {numChars.toLocaleString()}/{maxCharactersPerKB.toLocaleString()}
                </button>
              </div>
            </div>
          </div>

          {/* Running Jobs (excluding the selected chatbot since it's shown in header) */}
          {otherRunningJobs.length > 0 ? (
            <div className="flex flex-col">
              {otherRunningJobs.map((job) => (
                <div
                  key={job.job_id}
                  className="flex items-center justify-between px-v3-lg py-v3-sm  border-b border-v3-card-border hover:bg-v3-menu-hover cursor-pointer"
                  onClick={() => {
                    navigate(`/training/${job.job_id}`);
                    onClose?.();
                  }}
                >
                  <span className="text-sm font-medium text-v3-black-max">
                    {job.knowledge_base_name}
                  </span>
                  <div className="flex items-center gap-2 border border-v3-card-border rounded-v3-sm px-v3-xs py-[7px]">
                    <IconWrapper size={20}>
                      <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
                    </IconWrapper>
                    <span className="text-sm font-medium text-v3-purple-primary">Training...</span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* All Chatbots List */}
          {chatbots.length > 0 ? (
            <div className="flex flex-col max-h-[calc(100vh-420px)] overflow-y-auto">
              {chatbots
                .filter((chatbot) => {
                  const chatbotId = chatbot.kb_id || chatbot.id;
                  const selectedChatbotId = resolvedChatbot?.kb_id || resolvedChatbot?.knowledge_base?.id;
                  // Filter out the selected chatbot since it's already shown at the top
                  return chatbotId !== selectedChatbotId;
                })
                .map((chatbot) => {
                  const chatbotId = chatbot.kb_id || chatbot.id;
                  const chatbotName = chatbot.knowledge_base?.knowledge_base_name || chatbot.name || 'Unnamed Agent';

                  return (
                    <button
                      key={chatbotId}
                      onClick={() => {
                        navigate(`/chatbot/${chatbotId}`);
                        onClose?.();
                      }}
                      className="flex flex-col justify-center items-start self-stretch py-v3-md md:py-v3-xs px-v3-sm border-b border-v3-card-border transition-colors w-full hover:bg-v3-menu-hover text-left h-[80px] md:h-[48px]"
                    >
                      <span className="flex items-center text-v3-h9 font-medium truncate text-v3-black-max h-v3-lg">
                        {chatbotName}
                      </span>
                    </button>
                  );
                })}
            </div>
          ) : isLoadingChatbots ? (
            <div className="flex flex-col justify-center items-start self-stretch py-v3-md px-v3-sm">
              <div className="flex h-8 py-[7px] items-center gap-v3-xxs">
                <IconWrapper size={20}>
                  <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin" />
                </IconWrapper>
                <span className="text-v3-black-max text-v3-h10 font-[510]">
                  Loading more agents...
                </span>
              </div>
            </div>
          ) : null}

          {/* Create New Chatbot */}
          <div className="px-v3-lg py-v3-md text-center">
            <button
              onClick={() => {
                navigate('/chatbot/create');
                onClose?.();
              }}
              className="text-sm font-medium text-v3-purple-primary hover:opacity-80 transition-opacity"
            >
              + Create New Agent
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Per-user mode (original UI - dropdown)
  // Component to render training jobs dropdown (used for both Training button and spinning icon)
  const TrainingJobsMenuItems = ({ close }) => (
    <MenuItems
      anchor="bottom end"
      className="w-[400px] flex flex-col items-start rounded-v3-sm border border-v3-card-border bg-white shadow-v3-card max-h-64 overflow-y-auto z-50 focus:outline-none transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] data-[closed]:scale-95 data-[closed]:opacity-0 mt-1"
    >
      <div className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm h-12">
        <h3 className="text-v3-h9 font-bold text-v3-black-secondary">Currently training</h3>
      </div>
      <div className="flex flex-col w-full">
        {runningJobs.map((job) => (
          <MenuItem
            key={job.job_id}
            as="div"
            className="flex flex-col justify-center items-start self-stretch py-v3-xs px-v3-sm hover:bg-v3-menu-hover cursor-pointer"
            onClick={() => {
              navigate(`/training/${job.job_id}`);
              close?.();
            }}
          >
            <RunningJobCard job={job} />
          </MenuItem>
        ))}
      </div>
    </MenuItems>
  );

  // Check if chatbot is inactive
  const isChatbotInactive = resolvedChatbot?.active_chatbot === false;

  return (
    <div className="flex items-center gap-v3-xs whitespace-nowrap">
      <IconWrapper size={20}>
        <ChatIconTopNav className="w-full h-full text-v3-disabled-text" />
      </IconWrapper>
      <p className="text-v3-h10 font-medium text-v3-black-max">{resolvedChatbot?.knowledge_base?.knowledge_base_name || resolvedChatbot?.name || 'Agent'}</p>

      {isSelectedChatbotTraining ? (
        <Menu as="div" className="relative">
          {({ close }) => (
            <>
              <MenuButton className="group flex items-center gap-v3-xxs border border-v3-card-border rounded-v3-sm px-v3-xs py-v3-[7px] h-v3-lg cursor-pointer hover:border-v3-purple-medium transition-colors">
                <IconWrapper size={20}>
                  <RefreshIcon className="w-full h-full text-v3-purple-primary animate-spin group-hover:text-v3-purple-medium" />
                </IconWrapper>
                <p className="text-v3-h10 font-medium text-v3-purple-primary group-hover:text-v3-purple-medium">Training...</p>
              </MenuButton>
              <TrainingJobsMenuItems close={close} />
            </>
          )}
        </Menu>
      ) : isChatbotInactive ? (
        <ChatbotInactiveBadge />
      ) : (
        <div className="flex items-center gap-v3-xxs border border-v3-card-border rounded-v3-sm px-v3-xs py-v3-[7px] h-v3-lg">
          <IconWrapper size={20}>
            <StatusIcon className="w-full h-full text-v3-green-primary" />
          </IconWrapper>
          <p className="text-v3-h10 font-medium text-v3-black-max">Trained</p>
        </div>
      )}

      <Menu className="h-5">
        {({ open, close }) => (
          <div className="relative">
            <MenuButton className="text-black hover:bg-grey-1 rounded-v3-xxs transition-colors relative h-5">
              <IconWrapper size={20}>
                <ChevronDownIcon className={`w-full h-full text-v3-purple-primary hover:text-v3-purple-medium transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
              </IconWrapper>
            </MenuButton>
            <JobsMenuItems position="right" close={close} />
          </div>
        )}
      </Menu>
    </div>
  );
};

export default RunningJobsDropdown;
